/**
 * Gemeinsame Regeln für Portal-Zugänge (Hauptzentrale und Unternehmen).
 *
 * Die Prüfungen liegen bewusst an einer Stelle: Vorher hatten Absende-Knopf und
 * Submit-Handler in OperatorsPage je eigene, unterschiedlich strenge Regeln —
 * eine Falle bei jedem Umbau. Serverseitig prüfen die Cloud Functions dieselben
 * Bedingungen noch einmal verbindlich.
 */

export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

export const MIN_PASSWORD_LENGTH = 8;

export const CENTRAL_ACCOUNT_ROLES = ['admin', 'dispatcher'];
export const OPERATOR_ACCOUNT_ROLES = ['operator_admin', 'operator_staff'];

/**
 * Firebase Auth speichert die Adresse kleingeschrieben. Das users-Dokument muss
 * dieselbe Schreibweise tragen, sonst zeigt das Portal eine Adresse an, mit der
 * die Anmeldung nicht klappt.
 */
export const normalizeEmail = (value) => String(value || '').trim().toLowerCase();

export const isValidEmail = (value) => EMAIL_REGEX.test(normalizeEmail(value));

export const roleLabel = (role) => {
  switch (role) {
    case 'admin':
    case 'central_admin':
      return 'Admin';
    case 'dispatcher':
    case 'central_dispatch':
      return 'Disponent';
    case 'operator_admin':
      return 'Unternehmer-Admin';
    case 'operator_staff':
      return 'Unternehmer-Mitarbeiter';
    default:
      return role || 'Unbekannt';
  }
};

/**
 * Vorschlag für ein Initialpasswort.
 *
 * Bewusst ohne Sonderzeichen und ohne die verwechselbaren Zeichen 0/O/1/l/I:
 * Diese Passwörter werden bei den Verbundkunden regelmäßig telefonisch
 * durchgegeben oder abgetippt. Länge 12 gleicht die kleinere Zeichenmenge aus.
 */
export const generateInitialPassword = (length = 12) => {
  const alphabet = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';
  const values = new Uint32Array(length);
  window.crypto.getRandomValues(values);
  return Array.from(values, (value) => alphabet[value % alphabet.length]).join('');
};

/**
 * Validiert das Formular für einen neuen Zugang.
 * @returns {{valid: boolean, errors: {name?: string, email?: string, password?: string, operatorId?: string}}}
 */
export const validateAccountForm = (form, { requireOperator = false } = {}) => {
  const errors = {};

  if (!String(form.name || '').trim()) {
    errors.name = 'Bitte einen Namen angeben.';
  }
  if (!String(form.email || '').trim()) {
    errors.email = 'Bitte eine E-Mail-Adresse angeben.';
  } else if (!isValidEmail(form.email)) {
    errors.email = 'Die E-Mail-Adresse hat kein gültiges Format.';
  }
  if (String(form.password || '').length < MIN_PASSWORD_LENGTH) {
    errors.password = `Das Passwort muss mindestens ${MIN_PASSWORD_LENGTH} Zeichen lang sein.`;
  }
  if (requireOperator && !form.operatorId) {
    errors.operatorId = 'Bitte ein Unternehmen auswählen.';
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

/** Wie validateAccountForm, aber ohne Passwort (Bearbeiten ändert es nicht). */
export const validateAccountEditForm = (form) => {
  const errors = {};

  if (!String(form.name || '').trim()) {
    errors.name = 'Bitte einen Namen angeben.';
  }
  if (!String(form.email || '').trim()) {
    errors.email = 'Bitte eine E-Mail-Adresse angeben.';
  } else if (!isValidEmail(form.email)) {
    errors.email = 'Die E-Mail-Adresse hat kein gültiges Format.';
  }

  return { valid: Object.keys(errors).length === 0, errors };
};

/** Zugangsdaten als Text für die Zwischenablage (Übergabe an den Unternehmer). */
export const formatCredentials = ({ companyName, name, email, password, role }) => {
  const lines = [];
  if (companyName) lines.push(`Unternehmen: ${companyName}`);
  if (name) lines.push(`Name: ${name}`);
  lines.push(`E-Mail: ${email}`);
  lines.push(`Passwort: ${password}`);
  if (role) lines.push(`Rolle: ${roleLabel(role)}`);
  return lines.join('\n');
};
